import {
  Color,
  CustomBlending,
  DirectionalLight,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Vector2,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import { GTAOPass } from "../engine/postprocesses/GTAOPass.js";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass.js";
import { Entity, registerEvents } from "../engine/Engine";
import { SugarModel } from "../loader/SugarModel";
import { SugarModelGroup } from "../loader/SugarModelGroup";
import { getConfiguratorData } from "./ConfiguratorData";
import { logger } from "../util/Logger";

@registerEvents(
  "dragstart",
  "dragover",
  "drop",
  "rotate-end",
  "rotate-change",
  "replace",
  "model-selected",
  "model-deselected"
)
export class SugarConfiguratorEntity extends Entity {
  orbitControls?: OrbitControls;

  private _selectedModel?: SugarModel;
  private _type: "editor" | "configurator" | "both" = "configurator";
  configuratorId = "default";

  constructor(type: "editor" | "configurator") {
    super();
    this._type = type;
  }

  private async _begin() {
    const light = new DirectionalLight();
    this.engine?.activeScene?.add(light);

    this.initGTAOPass();
    this.initOutline();

    // ground
    const groundMesh = new Mesh(
      new PlaneGeometry(6000, 6000),
      new MeshBasicMaterial({
        color: 0xffffff,
        depthWrite: true,
        toneMapped: false,
      })
    );
    groundMesh.position.y = -0.1;
    groundMesh.rotation.x = -Math.PI / 2;
    groundMesh.name = "Ground Mesh";

    //this.engine?.activeScene?.add(groundMesh);

    this.engine!.dispatchCustomEvent("scene-changed", {
      detail: this.engine!.activeScene!,
    });
  }

  begin(): void {
    this._begin();
    this.enabled = true;
  }

  private _outlinePass?: OutlinePass; //CustomOutlinePass
  private _gtaoPass?: GTAOPass;

  private _syncGtaoPassSceneAndCamera(): void {
    if (!this._gtaoPass || !this.engine?.activeCamera) return;
    this._gtaoPass.scene = this.engine.getSceneForWebGLRender();
    this._gtaoPass.camera = this.engine.activeCamera.instance;
  }

  private _gtaoWebglSceneChangedBound = () => {
    this._syncGtaoPassSceneAndCamera();
  };

  private _gtaoUpdateCameraBound = () => {
    this._syncGtaoPassSceneAndCamera();
    if (this._gtaoPass) {
      this.engine?.removePass(this._gtaoPass);
      this.engine?.addPass(this._gtaoPass);
    }
  };

  private initGTAOPass() {
    if (!this._gtaoPass) {
      const aoParameters = {
        radius: 0.25,
        distanceExponent: 2,
        thickness: 0.5,
        scale: 1.25,
        samples: 16,
        distanceFallOff: 1,
        screenSpaceRadius: false,
      };
      const pdParameters = {
        lumaPhi: 10,
        depthPhi: 2,
        normalPhi: 3,
        radius: 4,
        radiusExponent: 1,
        rings: 2,
        samples: 16,
      };
      this._gtaoPass = new GTAOPass(
        this.engine!.getSceneForWebGLRender(),
        this.engine!.activeCamera!.instance,
        2048,
        2048,
      );
      this._gtaoPass!.updateGtaoMaterial(aoParameters);
      this._gtaoPass!.updatePdMaterial(pdParameters);

      this.engine?.removePass(this._gtaoPass);
      this.engine?.addPass(this._gtaoPass);
      this.engine?.addEventListener(
        "update-camera",
        this._gtaoUpdateCameraBound,
      );
      this.engine?.addEventListener(
        "webgl-render-scene-changed",
        this._gtaoWebglSceneChangedBound,
      );
    }
  }
  private initOutline() {
    if (!this._outlinePass) {
      //const { width, height } = this.engine!.canvas.getBoundingClientRect();
      this._outlinePass = new OutlinePass(
        new Vector2(512, 512),
        this.engine!.activeScene!,
        this.engine!.activeCamera!.instance
      );
      this._outlinePass.enabled = true;
      this._outlinePass.edgeThickness = 3;
      this._outlinePass.edgeStrength = 5;
      this._outlinePass.visibleEdgeColor = new Color(0x009587);
      this._outlinePass.hiddenEdgeColor = new Color(0x009587);
      this._outlinePass.overlayMaterial.blending = CustomBlending;

      this._outlinePass.pulsePeriod = 0;
    }
    this.engine?.removePass(this._outlinePass);
    this.engine?.addPass(this._outlinePass);
    this.engine?.addEventListener("update-camera", (e: any) => {
      this.engine?.removePass(this._outlinePass);
      this.engine?.addPass(this._outlinePass);
      if (this._outlinePass)
        this._outlinePass.renderCamera = this.engine!.activeCamera!.instance;
    });
  }

  get selectedModel() {
    return this._selectedModel;
  }

  async onDragstartOccured(event: DragEvent) {
    if (!event.dataTransfer) return;

    const baseModelData = JSON.parse(event.dataTransfer.getData("data-model"));
    const configData = getConfiguratorData(this.configuratorId);
    const baseModel = new SugarModelGroup(baseModelData, configData);

    logger.info("baseData", baseModelData);
    await baseModel.load(baseModelData);

    //events
    this._selectedModel = baseModel;
    this.engine?.activeScene?.add(baseModel!);

    this.engine?.dispatchCustomEvent("model-created", {
      detail: baseModel,
    });
    this.engine?.dispatchCustomEvent("model-added");
  }

  onModelSelectedOccured(e: { detail: SugarModel }) {
    this._selectedModel = e.detail;
    this._rotateYValue = this._selectedModel.rotation.y;
    if (this._outlinePass) {
      this._outlinePass.selectedObjects = [this._selectedModel];
    }
  }

  onModelDeselectedOccured(e: { detail: SugarModel }) {
    this._selectedModel = undefined;
    this._rotateYValue = 0;
    if (this._outlinePass) {
      this._outlinePass.selectedObjects = [];
    }
  }

  onDragoverOccured(event: DragEvent) {
    event.preventDefault();
  }

  onDropOccured(event: DragEvent) {
    event.preventDefault();
  }

  private _rotateYValue = 0;
  onRotateChangeOccured(e: any) {
    this._selectedModel?.rotation.set(
      0,
      -(this._rotateYValue + e.detail.progressTeta),
      0
    );
  }

  onRotateEndOccured(e: any) {
    this._rotateYValue += e.detail.progressTeta;
  }

  onReplaceOccured(e: any) {}

  animate(dt: number): void {
    this.orbitControls?.update(dt);
  }
}
